import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BadgeAward } from '../components/badges/BadgeFanfare';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { CommunityProjectResponse, Dashboard, Egg, HatchResult } from '../lib/types';
import { Button, EmptyState, ErrorState, Panel } from '../components/ui';
import { EggSlot, EmptyEggSlot, PokemonCard } from '../components/game';
import { HatchModal } from '../components/HatchModal';
import { formatNumber, timeAgo } from '../lib/format';
import { NewsTicker } from '../components/NewsTicker';
import { CommunityProjectPanel } from '../components/CommunityProjectPanel';
import { EventPanel } from '../components/EventPanel';
import { WorldBossPanel } from '../components/worldboss/WorldBossPanel';
import { TasksPanel } from '../components/economy/TasksPanel';

export function HomePage() {
  const { me, refresh, celebrate, celebrateBadges } = useSession();
  const { push } = useToast();
  const navigate = useNavigate();
  const state = useAsync<Dashboard>(() => api.get<Dashboard>('/me/dashboard'));
  // The two server-wide things a player should see the moment they log in.
  // Loaded here rather than folded into /me/dashboard because both are shared
  // state that changes on its own schedule — the dashboard is about *you*.
  const projects = useAsync<CommunityProjectResponse>(() => api.get('/community-projects'));
  const [hatching, setHatching] = useState<{ egg: Egg; result: HatchResult } | null>(null);
  const [busyEgg, setBusyEgg] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);

  const dashboard = state.data;

  async function hatch(egg: Egg) {
    setBusyEgg(egg.id);
    try {
      const result = await api.post<HatchResult>('/eggs/hatch', { eggId: egg.id });
      // Achievements are celebrated after the hatch reveal closes, never on top of it.
      setHatching({ egg, result });
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not hatch that egg.');
    } finally {
      setBusyEgg(null);
    }
  }

  async function claimDaily() {
    setClaiming(true);
    try {
      const result = await api.post<{ coins: number; tokens: number; unlocked: []; badges?: BadgeAward[] }>('/me/daily');
      push('reward', `Daily reward: +${result.coins} PokéCoins, +${result.tokens} Tokens`, '◆');
      celebrate(result.unlocked);
      celebrateBadges(result.badges ?? []);
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not claim.');
    } finally {
      setClaiming(false);
    }
  }

  async function visitRandom() {
    try {
      const result = await api.get<{ username: string | null }>('/community/random');
      if (result.username) navigate(`/trainer/${result.username}`);
      else push('info', 'No other trainers to visit yet. Invite a friend!');
    } catch {
      push('error', 'Could not find anyone right now.');
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const readyCount = dashboard?.hatchery.eggs.filter((egg) => egg.isReady).length ?? 0;
  const slots = dashboard?.hatchery.slots ?? 4;
  const filled = dashboard?.hatchery.eggs ?? [];

  return (
    <div className="stack">
      <TasksPanel />

      {/* Above the greeting on purpose: the first thing you should see on
          opening the game is that other people are playing it. */}
      <NewsTicker />

      <div className="page-head">
        <div>
          <h1 className="page-head__title">
            {greeting()}, {me?.profile.trainerName ?? 'Trainer'}
          </h1>
          <div className="page-head__sub">
            {readyCount > 0
              ? `${readyCount} egg${readyCount === 1 ? '' : 's'} ready to hatch!`
              : `Trainer Level ${me?.profile.level ?? 1} · ${me?.profile.speciesOwned ?? 0} species collected`}
          </div>
        </div>
        <div className="row row--wrap">
          {dashboard && !dashboard.daily.loginClaimed && (
            <Button variant="gold" onClick={() => void claimDaily()} loading={claiming}>
              ◆ Claim daily reward
            </Button>
          )}
          <Link className="btn btn--go" to="/grove">
            🌱 My Groves
          </Link>
          <Button onClick={() => void visitRandom()}>♥ Visit a Grove</Button>
        </div>
      </div>

      {/* --- Hatchery ------------------------------------------------------ */}
      <Panel
        title="◓ Your Hatchery"
        action={
          <Link className="btn btn--sm" to="/hatchery">
            Manage
          </Link>
        }
      >
        <div
          className="grid"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
        >
          {filled.map((egg) => (
            <EggSlot key={egg.id} egg={egg} onHatch={(e) => void hatch(e)} busy={busyEgg === egg.id} />
          ))}
          {Array.from({ length: Math.max(0, slots - filled.length) }, (_, index) => (
            <EmptyEggSlot key={`empty-${index}`} onClick={() => navigate('/hatchery')} />
          ))}
        </div>

        {dashboard && dashboard.hatchery.stored > 0 && (
          <p className="tiny muted center" style={{ marginTop: 'var(--gap-sm)' }}>
            {dashboard.hatchery.stored} egg{dashboard.hatchery.stored === 1 ? '' : 's'} waiting in storage.
          </p>
        )}
        {dashboard && dashboard.hatchery.stored === 0 && filled.length === 0 && (
          <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--gap)' }}>
            <Link className="btn btn--primary" to="/eggs">
              Get an egg
            </Link>
          </div>
        )}
      </Panel>

      {/* What the whole server is doing, above what you personally have to do.
          The community project and the running event are the two things that
          are true for everybody and easy to miss entirely. */}
      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start' }}
      >
        {/* Renders nothing when no event is running, so this costs the Home
            page no space on an ordinary day. */}
        {/* Above the ordinary event strip: a World Boss is time-limited in a
            way a hatch festival is not, and if both are on the one with a
            countdown and a leaderboard is the one to see first. */}
        <WorldBossPanel />
        <EventPanel />
        <CommunityProjectPanel
          project={projects.data?.project ?? null}
          season={projects.data?.season}
          compact
        />
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start' }}
      >
        {/* --- Today ------------------------------------------------------- */}
        <Panel title="◈ Today">
          <div className="stack">
            <TodoRow
              done={dashboard?.daily.loginClaimed ?? false}
              label="Claim your daily reward"
              action={
                dashboard && !dashboard.daily.loginClaimed ? (
                  <Button size="sm" variant="gold" onClick={() => void claimDaily()} loading={claiming}>
                    Claim
                  </Button>
                ) : null
              }
            />
            <TodoRow
              done={dashboard?.daily.freeEggClaimed ?? false}
              label="Collect today's free egg"
              action={
                dashboard && !dashboard.daily.freeEggClaimed ? (
                  <Link className="btn btn--sm btn--go" to="/eggs">
                    Collect
                  </Link>
                ) : null
              }
            />

            <div className="stack-sm">
              <div className="row row--between tiny mono">
                <span>Helped today</span>
                <span className="muted">{dashboard?.helpedToday ?? 0}</span>
              </div>
              <p className="tiny muted">
                Feeding other trainers' Pokémon earns you Friendship Tokens and PokéCoins. There's
                no daily limit — each Pokémon just needs ten minutes between feeds.
              </p>
              <Link className="btn btn--sm btn--go btn--block" to="/community">
                Find Groves to visit
              </Link>
            </div>
          </div>
        </Panel>

        {/* --- Activity ---------------------------------------------------- */}
        <Panel title="♥ Community activity" hint="What others did for you">
          {dashboard && dashboard.activity.length > 0 ? (
            <div className="stack-sm">
              {dashboard.activity.map((entry) => (
                <div key={entry.id} className="row" style={{ gap: 'var(--gap-sm)' }}>
                  {entry.pokemonSprite && (
                    <img
                      className="pixel"
                      src={entry.pokemonSprite}
                      alt=""
                      style={{ width: 34, height: 34, flexShrink: 0 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="tiny">
                      <Link to={`/trainer/${entry.actorName}`}>{entry.actorName}</Link>
                      {entry.actorIsNpc && <span className="chip chip--npc chip--sm" style={{ marginLeft: 4 }}>🌿</span>}{' '}
                      {entry.type === 'WARM_EGG'
                        ? 'warmed one of your eggs'
                        : `fed ${entry.pokemonName ?? 'your Pokémon'}`}
                    </div>
                    <div className="tiny muted">
                      {entry.expGranted > 0 ? `+${formatNumber(entry.expGranted)} EXP · ` : ''}
                      {timeAgo(entry.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState art="♡" title="Nothing yet">
              Put some Pokémon on your profile, then visit other trainers — most people help back.
            </EmptyState>
          )}
        </Panel>
      </div>

      {/* --- Recent ------------------------------------------------------- */}
      <Panel
        title="◈ Recently obtained"
        action={
          <Link className="btn btn--sm" to="/collection">
            View all
          </Link>
        }
      >
        {state.loading ? (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
            {Array.from({ length: 6 }, (_, index) => (
              <div key={index} className="pxwell skeleton" style={{ height: 160 }} />
            ))}
          </div>
        ) : dashboard && dashboard.recentPokemon.length > 0 ? (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
            {dashboard.recentPokemon.map((pokemon) => (
              <PokemonCard key={pokemon.id} pokemon={pokemon} to={`/pokemon/${pokemon.id}`} />
            ))}
          </div>
        ) : (
          <EmptyState
            art="◓"
            title="No Pokémon yet"
            action={
              <Link className="btn btn--primary" to="/eggs">
                Get your first egg
              </Link>
            }
          >
            Every Pokémon in PokéGrove comes from an egg. Grab one and put it in your hatchery.
          </EmptyState>
        )}
      </Panel>

      {hatching && (
        <HatchModal
          egg={hatching.egg}
          result={hatching.result}
          onClose={() => {
            const unlocked = hatching.result.unlocked;
            const badges = hatching.result.badges ?? [];
            setHatching(null);
            celebrate(unlocked);
            celebrateBadges(badges);
          }}
        />
      )}
    </div>
  );
}

function TodoRow({ done, label, action }: { done: boolean; label: string; action: React.ReactNode }) {
  return (
    <div className="row row--between">
      <span className="row" style={{ gap: 8 }}>
        <span
          className="mono"
          style={{ color: done ? 'var(--leaf-dark)' : 'var(--ink-faint)', fontSize: '1rem' }}
          aria-hidden
        >
          {done ? '☑' : '☐'}
        </span>
        <span
          className="tiny"
          style={{ textDecoration: done ? 'line-through' : 'none', color: done ? 'var(--ink-faint)' : 'inherit' }}
        >
          {label}
        </span>
      </span>
      {action}
    </div>
  );
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
